import { useNavigationManager } from '@Router/useNavigationManager';
import { z } from 'zod';
import { AccountantPersonalDetailsFormType } from '../AccountantPersonalDetailsForm';
import { useFormHook, useSetError } from '@Hooks/index';
import { OnboardingFormFields } from '@Types/index';
import { firstNameScheme } from '@Components/inputs/FirstNameInput';
import { lastNameScheme } from '@Components/inputs/LastNameInput';
import { phoneFormScheme } from '@Pages/happyFlowPages/phone/phoneForm/phoneFormScheme';
import { useFormState } from 'react-hook-form';
import { useBoundStore } from '@Store/useBoundStore';
import { useStartAuthorizedUserOnboarding } from '../api/authorizedUser.api';
import { useCreateFullPageSnapShot } from '@Services/snapShot/useCreateFullPageSnapShot';
import { useState } from 'react';

const accountantPersonalDetailsSchema = z.object({
  ...firstNameScheme.shape,
  ...lastNameScheme.shape,
  ...phoneFormScheme.shape,
});

export const useAccountantPersonalDetailsLogic = () => {
  const { navigateToNextPage } = useNavigationManager();
  const { getCustomerData, setCustomerData, setConsentFormData } = useBoundStore((state) => state);
  const { mutateAsync: saveDetails } = useStartAuthorizedUserOnboarding();
  const { takeScreenShot } = useCreateFullPageSnapShot();
  const [isLoading, setIsLoading] = useState(false);

  const defaultValues: AccountantPersonalDetailsFormType = {
    [OnboardingFormFields.FIRST_NAME]: '',
    [OnboardingFormFields.LAST_NAME]: '',
    [OnboardingFormFields.PHONE_NUMBER]: '',
  };

  const formMethods = useFormHook({
    formSchema: accountantPersonalDetailsSchema,
    formDefaultValues: defaultValues,
    schemaTitle: 'invite-accountant-personal-details-form',
  });

  const { isValid } = useFormState({ control: formMethods.control });

  const onSubmit = async (data: AccountantPersonalDetailsFormType) => {
    try {
      setIsLoading(true);
      await saveDetails({
        firstName: data[OnboardingFormFields.FIRST_NAME],
        lastName: data[OnboardingFormFields.LAST_NAME],
        phoneNumber: data[OnboardingFormFields.PHONE_NUMBER],
      });
      const consentFormData = await takeScreenShot({
        nodes: [
          document.querySelector('#page-content') as HTMLElement,
          document.querySelector('#page-footer') as HTMLElement,
        ],
      });

      if (consentFormData) {
        setConsentFormData(consentFormData);
      }

      const customerDataToUpdate = getCustomerData();
      customerDataToUpdate.customerData.firstName = data[OnboardingFormFields.FIRST_NAME];
      customerDataToUpdate.customerData.lastName = data[OnboardingFormFields.LAST_NAME];
      customerDataToUpdate.customerData.phoneNumber = data[OnboardingFormFields.PHONE_NUMBER];
      setCustomerData(customerDataToUpdate);

      setIsLoading(false);
      navigateToNextPage();
    } catch (error) {
      setIsLoading(false);
      console.error('Failed to start authorized user onboarding:', error);
    }
  };

  useSetError({ formMethods });

  return {
    formMethods,
    isValid,
    handleSubmit: formMethods.handleSubmit(onSubmit),
    isLoading,
  };
};
